import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, formatDate, timeAgo, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, StatusPill, PriorityPill } from "@/components/shared/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Play, Pause, CheckCircle, Warning, ArrowLeft, Clock, User, Broadcast, Paperclip, DownloadSimple, Trash, UploadSimple } from "@phosphor-icons/react";

export default function CaseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [comment, setComment] = useState("");
  const [employees, setEmployees] = useState([]);
  const [exceptionType, setExceptionType] = useState("priority_change");
  const [exceptionReason, setExceptionReason] = useState("");
  const [reqPriority, setReqPriority] = useState("high");
  const [reqAssignee, setReqAssignee] = useState("");
  const [simResults, setSimResults] = useState([]);
  const [simOpen, setSimOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/cases/${id}`);
    setC(data);
  };

  useEffect(() => {
    load().catch(() => toast.error("Case not found"));
    api.get("/employees").then(r => setEmployees(r.data.items)).catch(() => {});
  }, [id]);

  if (!c) return <div className="text-sm text-muted-foreground mono">Loading…</div>;
  const isMgr = user?.role === "manager";
  const isAssignee = c.assignee_id && employees.find(e => e.id === c.assignee_id)?.user_id === user?.id;

  const changeStatus = async (status) => {
    try {
      await api.patch(`/cases/${id}`, { status });
      toast.success(`Status → ${status.replace("_", " ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/cases/${id}/comments`, { text: comment });
      setComment("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitException = async () => {
    try {
      const payload = { case_id: id, type: exceptionType, reason: exceptionReason };
      if (exceptionType === "priority_change") payload.requested_priority = reqPriority;
      if (exceptionType === "reassignment") payload.requested_assignee = reqAssignee;
      await api.post("/exceptions", payload);
      toast.success("Exception raised");
      setExceptionReason("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runSimulation = async () => {
    try {
      const { data } = await api.get(`/allocation/simulate/${id}`);
      setSimResults(data.results);
      setSimOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const assignManual = async (emp_id) => {
    try {
      await api.post("/allocation/manual", { case_id: id, assignee_id: emp_id, override_reason: "Manual assign from simulator" });
      toast.success("Assigned");
      setSimOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await fetch(`${API}/cases/${id}/attachments`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      toast.success(`Uploaded ${file.name}`);
      load();
    } catch (e) { toast.error(e.message || "Upload failed"); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const deleteAttachment = async (attId) => {
    try {
      await api.delete(`/attachments/${attId}`);
      toast.success("Attachment removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const fmtBytes = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div>
      <Button data-testid="case-back-btn" variant="ghost" size="sm" onClick={() => nav("/cases")} className="mb-3">
        <ArrowLeft size={14} className="mr-1" /> Back to cases
      </Button>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="eyebrow text-muted-foreground">{c.case_type}</div>
          <div className="flex items-center gap-3 mt-1">
            <h1 className="display text-3xl font-semibold tracking-tighter mono">{c.case_id}</h1>
            <StatusPill status={c.status} />
            <PriorityPill priority={c.priority} />
          </div>
          <div className="text-sm text-muted-foreground mt-1">Customer: <span className="text-foreground font-medium">{c.customer}</span> · Team: {c.team || "—"}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(isMgr || isAssignee) && c.status === "assigned" && (
            <Button data-testid="case-start-btn" onClick={() => changeStatus("in_progress")} className="h-9">
              <Play size={14} className="mr-1" /> Start
            </Button>
          )}
          {(isMgr || isAssignee) && c.status === "in_progress" && (
            <>
              <Button data-testid="case-pause-btn" variant="outline" onClick={() => changeStatus("paused")} className="h-9">
                <Pause size={14} className="mr-1" /> Pause
              </Button>
              <Button data-testid="case-complete-btn" onClick={() => changeStatus("completed")} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white">
                <CheckCircle size={14} className="mr-1" /> Complete
              </Button>
            </>
          )}
          {(isMgr || isAssignee) && c.status === "paused" && (
            <Button data-testid="case-resume-btn" onClick={() => changeStatus("in_progress")} className="h-9">
              <Play size={14} className="mr-1" /> Resume
            </Button>
          )}
          {isMgr && (
            <Button data-testid="case-simulate-btn" variant="outline" className="h-9" onClick={runSimulation}>
              <Broadcast size={14} className="mr-1" /> Simulate re-allocation
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="border rounded-md p-5 bg-card">
            <div className="eyebrow text-muted-foreground mb-3">Description</div>
            <div className="text-sm">{c.description || <em className="text-muted-foreground">No description</em>}</div>
          </div>

          <Tabs defaultValue="timeline" className="w-full">
            <TabsList data-testid="case-tabs" className="h-auto">
              <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
              <TabsTrigger value="comments" data-testid="tab-comments">Comments</TabsTrigger>
              <TabsTrigger value="assignments" data-testid="tab-assignments">Assignments</TabsTrigger>
              <TabsTrigger value="exception" data-testid="tab-exception">Raise Exception</TabsTrigger>
            </TabsList>

            <TabsContent value="timeline">
              <div className="border rounded-md bg-card p-5">
                <div className="space-y-3">
                  {c.timeline?.map((t, i) => (
                    <div key={t.id} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-2.5 h-2.5 rounded-full bg-primary mt-1.5" />
                        {i < c.timeline.length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                      </div>
                      <div className="pb-4">
                        <div className="text-sm font-medium">{t.message}</div>
                        <div className="text-[10px] mono text-muted-foreground">{formatDate(t.at)} · {t.event}</div>
                      </div>
                    </div>
                  ))}
                  {(!c.timeline || c.timeline.length === 0) && <div className="text-sm text-muted-foreground">No activity yet</div>}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="comments">
              <div className="border rounded-md bg-card p-5 space-y-4">
                <div className="space-y-3">
                  {c.comments?.map(cm => (
                    <div key={cm.id} className="border-l-2 border-primary/50 pl-3">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{cm.author_name}</span>
                        <span className="mono">· {timeAgo(cm.at)}</span>
                      </div>
                      <div className="text-sm mt-0.5">{cm.text}</div>
                    </div>
                  ))}
                  {(!c.comments || c.comments.length === 0) && <div className="text-sm text-muted-foreground">No comments yet</div>}
                </div>
                <div className="flex gap-2">
                  <Textarea data-testid="comment-input" value={comment} onChange={e => setComment(e.target.value)} placeholder="Add a comment…" className="min-h-[60px]" />
                  <Button data-testid="comment-submit-btn" onClick={submitComment}>Post</Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="assignments">
              <div className="border rounded-md bg-card p-5">
                <div className="space-y-2">
                  {c.assignments?.map(a => (
                    <div key={a.id} className="flex items-center justify-between text-sm border-b py-2 last:border-b-0">
                      <div>
                        <div className="font-medium">{a.assignee_name}</div>
                        <div className="text-xs text-muted-foreground mono">by {a.assigned_by === "auto" ? "AUTO" : a.manual_override ? "MANUAL" : a.assigned_by} · score {a.score?.toFixed?.(2) ?? "—"}</div>
                      </div>
                      <div className="text-xs text-muted-foreground mono">{formatDate(a.assigned_at)}</div>
                    </div>
                  ))}
                  {(!c.assignments || c.assignments.length === 0) && <div className="text-sm text-muted-foreground">Not yet assigned</div>}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="attachments">
              <div className="border rounded-md bg-card p-5">
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                  <div className="text-sm text-muted-foreground">Max 15 MB per file. PDF, images, docs, spreadsheets and archives are supported.</div>
                  <div>
                    <input
                      ref={fileInputRef}
                      data-testid="attachment-file-input"
                      type="file"
                      className="hidden"
                      onChange={(e) => uploadFile(e.target.files?.[0])}
                    />
                    <Button data-testid="attachment-upload-btn" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                      <UploadSimple size={14} className="mr-1" /> {uploading ? "Uploading…" : "Upload file"}
                    </Button>
                  </div>
                </div>
                <div className="divide-y">
                  {c.attachments?.map(a => (
                    <div key={a.id} data-testid={`attachment-${a.id}`} className="flex items-center justify-between py-3 gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded bg-secondary text-muted-foreground grid place-items-center shrink-0">
                          <Paperclip size={16} />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{a.original_name}</div>
                          <div className="text-[11px] mono text-muted-foreground">{fmtBytes(a.size_bytes)} · uploaded by {a.uploaded_by_name} · {timeAgo(a.uploaded_at)}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <a
                          data-testid={`download-${a.id}`}
                          href={`${API}/attachments/${a.id}/download`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-8 w-8 grid place-items-center rounded hover:bg-secondary text-muted-foreground"
                          title="Download"
                        >
                          <DownloadSimple size={16} />
                        </a>
                        {isMgr && (
                          <button
                            data-testid={`delete-${a.id}`}
                            onClick={() => deleteAttachment(a.id)}
                            className="h-8 w-8 grid place-items-center rounded hover:bg-destructive/10 text-destructive"
                            title="Delete"
                          >
                            <Trash size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {(!c.attachments || c.attachments.length === 0) && (
                    <div className="text-sm text-muted-foreground text-center py-6">No attachments yet</div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="exception">
              <div className="border rounded-md bg-card p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="eyebrow text-muted-foreground">Type</Label>
                    <Select value={exceptionType} onValueChange={setExceptionType}>
                      <SelectTrigger data-testid="exception-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="priority_change">Priority change</SelectItem>
                        <SelectItem value="complexity_change">Complexity change</SelectItem>
                        <SelectItem value="escalation">Escalation</SelectItem>
                        <SelectItem value="wrong_allocation">Wrong allocation</SelectItem>
                        <SelectItem value="customer_vulnerability">Customer vulnerability</SelectItem>
                        <SelectItem value="reassignment">Reassignment</SelectItem>
                        <SelectItem value="reopen">Reopen case</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {exceptionType === "priority_change" && (
                    <div>
                      <Label className="eyebrow text-muted-foreground">Requested priority</Label>
                      <Select value={reqPriority} onValueChange={setReqPriority}>
                        <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["low", "medium", "high", "critical"].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {exceptionType === "reassignment" && (
                    <div>
                      <Label className="eyebrow text-muted-foreground">Reassign to</Label>
                      <Select value={reqAssignee} onValueChange={setReqAssignee}>
                        <SelectTrigger className="mt-1.5"><SelectValue placeholder="Choose employee" /></SelectTrigger>
                        <SelectContent>
                          {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} · {e.team}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="eyebrow text-muted-foreground">Reason</Label>
                  <Textarea data-testid="exception-reason" value={exceptionReason} onChange={e => setExceptionReason(e.target.value)} className="mt-1.5" placeholder="Provide justification for the exception…" />
                </div>
                <Button data-testid="raise-exception-btn" onClick={submitException} disabled={!exceptionReason}>Raise exception</Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-4">
          <div className="border rounded-md p-5 bg-card">
            <div className="eyebrow text-muted-foreground mb-3">Details</div>
            <dl className="space-y-2 text-sm">
              <Row label="Assignee" value={c.assignee_name || "—"} icon={User} />
              <Row label="Priority" value={<PriorityPill priority={c.priority} />} />
              <Row label="Complexity" value={<span className="capitalize">{c.complexity}</span>} />
              <Row label="Required skill" value={c.required_skill} />
              <Row label="Customer tier" value={<span className="capitalize">{c.customer_tier}</span>} />
              <Row label="Vulnerable" value={c.vulnerable ? "Yes" : "No"} />
              <Row label="Estimated effort" value={`${c.estimated_effort_min} min`} />
              <Row label="SLA" value={`${c.sla_hours}h`} icon={Clock} />
              <Row label="Due" value={formatDate(c.due_date)} />
              <Row label="Created" value={formatDate(c.created_at)} />
              {c.allocation_score != null && <Row label="Allocation score" value={c.allocation_score.toFixed?.(3) ?? c.allocation_score} />}
            </dl>
          </div>
        </div>
      </div>

      {/* Simulation dialog */}
      <Dialog open={simOpen} onOpenChange={setSimOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Allocation simulation</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-widest text-muted-foreground border-b">
                <tr>
                  <th className="px-3 py-2">Rank</th>
                  <th className="px-3 py-2">Employee</th>
                  <th className="px-3 py-2">Team</th>
                  <th className="px-3 py-2">Score</th>
                  <th className="px-3 py-2">Availability</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {simResults.map((r, i) => (
                  <tr key={r.employee_id} className={i === 0 ? "bg-primary/5" : ""}>
                    <td className="px-3 py-2 mono">#{i + 1}</td>
                    <td className="px-3 py-2 font-medium">{r.name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.team}</td>
                    <td className="px-3 py-2 mono">{r.score >= 0 ? r.score.toFixed(3) : "excluded"}</td>
                    <td className="px-3 py-2 text-xs capitalize">{r.availability}</td>
                    <td className="px-3 py-2 text-right">
                      {isMgr && r.score >= 0 && (
                        <Button size="sm" data-testid={`sim-assign-${r.employee_id}`} onClick={() => assignManual(r.employee_id)}>Assign</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, icon: Icon }) {
  return (
    <div className="flex justify-between items-center gap-3">
      <dt className="text-xs text-muted-foreground flex items-center gap-1.5">{Icon && <Icon size={12} />} {label}</dt>
      <dd className="text-sm font-medium text-right">{value}</dd>
    </div>
  );
}
